import React, { Component } from "react";
import { Modal, Form, Button, Spinner } from "react-bootstrap";
import gql from "graphql-tag";
import * as Cookies from "es-cookie";
import CardLessDataTable from "../datatables/CardLessDataTableWithSearch";
import "./_expenseModal.scss";
import { attachModuleGuard, AccessDeniedModal, AccessDeniedCard } from "../../../utils/moduleGuard";

// /expense-management → "expense_management". Match on module_key only.
// This tab owns the densest write surface in the module: create/edit/delete for both
// Expense Heads/Categories and Vendors — and the only two real deletes.
const EXPENSE_MODULE = ["expense_management"];

// ─── GraphQL: Expense Heads ────────────────────────────────────────────────
const CREATE_EXPENSE_HEAD = gql`
  mutation ($input: ExpenseHeadInput!) {
    createExpenseHead(input: $input) {
      id
      expense_head
      description
      estatus
    }
  }
`;

const UPDATE_EXPENSE_HEAD = gql`
  mutation ($input: ExpenseHeadInput!) {
    updateExpenseHead(input: $input) {
      id
      expense_head
      description
      estatus
    }
  }
`;

const DELETE_EXPENSE_HEAD = gql`
  mutation ($id: ID!) {
    deleteExpenseHead(id: $id) {
      id
    }
  }
`;

// ─── GraphQL: Expense Categories ───────────────────────────────────────────
const CREATE_EXPENSE_CATEGORY = gql`
  mutation ($input: ExpenseCategoryInput!) {
    createExpenseCategory(input: $input) {
      id
      expense_categories
      description
      estatus
    }
  }
`;

const UPDATE_EXPENSE_CATEGORY = gql`
  mutation ($input: ExpenseCategoryInput!) {
    updateExpenseCategory(input: $input) {
      id
      expense_categories
      description
      estatus
    }
  }
`;

const DELETE_EXPENSE_CATEGORY = gql`
  mutation ($id: ID!) {
    deleteExpenseCategory(id: $id) {
      id
    }
  }
`;

// ─── GraphQL: Vendors ──────────────────────────────────────────────────────
const CREATE_VENDOR = gql`
  mutation ($input: VendorInput!) {
    createVendor(input: $input) {
      id
      vendor_name
    }
  }
`;

const UPDATE_VENDOR = gql`
  mutation ($input: VendorInput!) {
    updateVendor(input: $input) {
      id
      vendor_name
    }
  }
`;

const DELETE_VENDOR = gql`
  mutation ($id: ID!) {
    deleteVendor(id: $id) {
      id
    }
  }
`;

// ─── Theme ─────────────────────────────────────────────────────────────────
const THEME = {
  bgCard: "#ffffff",
  bgLight: "#f8fafc",
  primary: "#6366f1",
  success: "#10b981",
  warning: "#f59e0b",
  danger: "#ef4444",
  text: "#0f172a",
  textMuted: "#64748b",
  border: "#e2e8f0",
};

// ─── StatusBadge ───────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const active = status === 1 || status === "1";
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: "20px",
        fontSize: "11px",
        fontWeight: "700",
        background: active ? "#dcfce7" : "#fee2e2",
        color: active ? "#059669" : "#dc2626",
      }}
    >
      {active ? "Active" : "Inactive"}
    </span>
  );
};

// ─── ExpenseTypes (merged with Vendor Management) ─────────────────────────
class ExpenseTypes extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // Shared tab state
      subTab: "heads", // 'heads' | 'categories' | 'vendors'

      // ── Category / Sub-Category modal state ──
      showModal: false,
      name: "",
      description: "",
      selectedHeadId: "", // parent Category for a Sub Category
      saving: false,
      editItem: null,
      successMsg: "", // in-UI success banner (replaces alert popups)
      errors: {},
      warnings: {},

      // ── Vendor modal state ──
      vendorShowModal: false,
      vendorEditItem: null,
      vendorFormData: {
        vendor_name: "",
        contact_person: "",
        contact_no: "",
        address: "",
        comments: "",
      },
      vendorSaving: false,
      vendorErrors: {},
      vendorWarnings: {},
      accessDeniedShow: false,
    };

    this.guard = attachModuleGuard(this, EXPENSE_MODULE);
  }

  // ══════════════════════════════════════════════════════════════════════════
  //  Category / Sub-Category — Validation
  // ══════════════════════════════════════════════════════════════════════════

  validateField = (name, value) => {
    const errors = { ...this.state.errors };
    const warnings = { ...this.state.warnings };

    if (name === "name") {
      if (!value || !value.trim()) {
        errors.name = "Name is required";
      } else if (value.trim().length < 2) {
        errors.name = "Name must be at least 2 characters";
      } else if (value.length > 45) {
        errors.name = "Name cannot exceed 45 characters";
      } else if (!/^[a-zA-Z ]+$/.test(value)) {
        errors.name = "Only letters and spaces allowed";
      } else {
        delete errors.name;
      }
    }

    if (name === "description") {
      if (value && value.length > 500) {
        errors.description = "Description cannot exceed 500 characters";
      } else {
        delete errors.description;
      }
    }

    this.setState({ errors, warnings });
  };

  validateForm = () => {
    const { name, description, subTab, selectedHeadId } = this.state;
    const errors = {};

    if (!name || !name.trim()) {
      errors.name = "Name is required";
    } else if (name.trim().length < 2) {
      errors.name = "Name must be at least 2 characters";
    } else if (name.length > 45) {
      errors.name = "Name cannot exceed 45 characters";
    } else if (!/^[a-zA-Z ]+$/.test(name)) {
      errors.name = "Only letters and spaces allowed";
    }

    // A Sub Category must belong to a Category
    if (subTab !== "heads" && !selectedHeadId) {
      errors.selectedHeadId = "Please select a Category";
    }

    if (description && description.length > 500) {
      errors.description = "Description cannot exceed 500 characters";
    }

    return errors;
  };

  // ══════════════════════════════════════════════════════════════════════════
  //  Category / Sub-Category — Columns & Handlers
  // ══════════════════════════════════════════════════════════════════════════

  getColumns() {
    const { subTab } = this.state;
    const cols = [
      {
        dataField: "id",
        text: "#",
        formatter: (cell, row, rowIndex) => rowIndex + 1,
        sort: true,
        headerStyle: { width: "110px", textAlign: "center" },
        style: { textAlign: "center" },
      },
      {
        dataField: subTab === "heads" ? "expense_head" : "expense_categories",
        text: "Name",
        sort: true,
        formatter: (cell) => cell,
      },
    ];

    if (subTab !== "heads") {
      cols.push({
        dataField: "expense_head_id",
        text: "Category",
        sort: true,
        formatter: (cell) => {
          const head = (this.props.configs?.getExpenseHeads || []).find(h => Number(h.id) === Number(cell));
          return head ? (
            <span style={{ background: "#eef2ff", color: "#4f46e5", borderRadius: 20, padding: "2px 10px", fontSize: 12, fontWeight: 600 }}>{head.expense_head}</span>
          ) : "—";
        },
      });
      cols.push({
        dataField: "description",
        text: "Description",
        sort: true,
        formatter: (cell) => cell || "—",
      });
    }

    cols.push(
      {
        dataField: "estatus",
        text: "Status",
        sort: true,
        headerStyle: { width: "140px", textAlign: "center" },
        style: { textAlign: "center" },
        formatter: (cell) => <StatusBadge status={cell} />,
      },
      {
        dataField: "actions",
        text: "Actions",
        headerStyle: { width: "120px", textAlign: "center" },
        style: { textAlign: "center" },
        formatter: (cell, row) => (
          <div>
            <button
              onClick={() => this.handleOpenModal(row)}
              style={{
                background: "none",
                border: "none",
                color: "#6366f1",
                marginRight: "12px",
                cursor: "pointer",
              }}
            >
              <i className="fal fa-edit" />
            </button>
            <button
              onClick={() =>
                this.handleDelete(
                  row.id,
                  subTab === "heads" ? row.expense_head : row.expense_categories
                )
              }
              style={{
                background: "none",
                border: "none",
                color: "#ef4444",
                cursor: "pointer",
              }}
            >
              <i className="fal fa-trash-alt" />
            </button>
          </div>
        ),
      }
    );

    return cols;
  }

  // Opens the create/edit modal for an Expense Head or Category. Gated before the modal
  // opens; handleSubmit re-checks at the write. Button stays visible and clickable.
  handleOpenModal = (item = null) => {
    if (!this.guard.requireEdit()) return;

    if (item && item.id) {
      this.setState({
        showModal: true,
        editItem: item,
        name:
          this.state.subTab === "heads"
            ? item.expense_head
            : item.expense_categories,
        description: item.description || "",
        selectedHeadId: item.expense_head_id ? String(item.expense_head_id) : "",
        errors: {},
        warnings: {},
      });
    } else {
      this.setState({
        showModal: true,
        editItem: null,
        name: "",
        description: "",
        selectedHeadId: "",
        errors: {},
        warnings: {},
      });
    }
  };

  handleCloseModal = () => {
    this.setState({
      showModal: false,
      editItem: null,
      name: "",
      description: "",
      selectedHeadId: "",
      errors: {},
      warnings: {},
    });
  };

  // Checked BEFORE the confirm prompt: a role without is_delete should be told they lack
  // access, not asked to confirm a delete that can't happen.
  handleDelete = async (id, itemName) => {
    if (!this.guard.requireDelete()) return;

    const { subTab } = this.state;
    const { client, onUpdate } = this.props;
    const typeLabel = subTab === "heads" ? "Category" : "Sub Category";
    if (
      !window.confirm(
        `Are you sure you want to delete this ${typeLabel} "${itemName}"?\nThis action cannot be undone.`
      )
    )
      return;

    try {
      if (subTab === "heads") {
        await client.mutate({ mutation: DELETE_EXPENSE_HEAD, variables: { id } });
      } else {
        await client.mutate({ mutation: DELETE_EXPENSE_CATEGORY, variables: { id } });
      }
      alert(`${typeLabel} deleted successfully!`);
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error(err);
      alert(`Failed to delete ${typeLabel}: ` + err.message);
    }
  };

  handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "name") {
      const filtered = value.replace(/[^a-zA-Z ]/g, "");
      this.setState({ [name]: filtered }, () => {
        this.validateField(name, filtered);
      });
      return;
    }
    this.setState({ [name]: value }, () => {
      this.validateField(name, value);
    });
  };

  componentWillUnmount() {
    if (this._successTimer) clearTimeout(this._successTimer);
  }

  showSuccess = (msg) => {
    this.setState({ successMsg: msg });
    if (this._successTimer) clearTimeout(this._successTimer);
    this._successTimer = setTimeout(() => this.setState({ successMsg: "" }), 3000);
  };

  // Last gate before the head/category create-or-update mutation leaves the client.
  handleSubmit = async (e) => {
    e.preventDefault();

    if (!this.guard.requireEdit()) return;

    const errors = this.validateForm();
    if (Object.keys(errors).length > 0) {
      this.setState({ errors });
      return;
    }

    const { name, description, subTab, editItem, selectedHeadId } = this.state;
    const { client, onUpdate } = this.props;

    const instituteId = parseInt(Cookies.get("institutionid") || "0");
    if (!instituteId) {
      alert("Institution ID not found. Please log in again.");
      return;
    }

    this.setState({ saving: true });

    try {
      if (subTab === "heads") {
        if (editItem) {
          await client.mutate({
            mutation: UPDATE_EXPENSE_HEAD,
            variables: {
              input: {
                id: editItem.id,
                expense_head: name,
                description: description || "",
                institute_id: instituteId,
              },
            },
          });
          alert("Category updated successfully!");
        } else {
          await client.mutate({
            mutation: CREATE_EXPENSE_HEAD,
            variables: {
              input: {
                expense_head: name,
                description: description || "",
                institute_id: instituteId,
              },
            },
          });
          alert("Category added successfully!");
        }
      } else {
        if (editItem) {
          await client.mutate({
            mutation: UPDATE_EXPENSE_CATEGORY,
            variables: {
              input: {
                id: editItem.id,
                expense_categories: name,
                expense_head_id: selectedHeadId ? parseInt(selectedHeadId) : null,
                description: description || "",
                institute_id: instituteId,
              },
            },
          });
          this.showSuccess("Sub Category updated successfully!");
        } else {
          await client.mutate({
            mutation: CREATE_EXPENSE_CATEGORY,
            variables: {
              input: {
                expense_categories: name,
                expense_head_id: selectedHeadId ? parseInt(selectedHeadId) : null,
                description: description || "",
                institute_id: instituteId,
              },
            },
          });
          alert("Sub Category added successfully!");
        }
      }

      this.setState({
        showModal: false,
        editItem: null,
        name: "",
        description: "",
        saving: false,
      });
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error(err);
      alert("Failed to save: " + err.message);
      this.setState({ saving: false });
    }
  };

  // ══════════════════════════════════════════════════════════════════════════
  //  Vendor — Validation
  // ══════════════════════════════════════════════════════════════════════════

  validateVendorField = (name, value) => {
    const errors = { ...this.state.vendorErrors };
    const warnings = { ...this.state.vendorWarnings };

    if (name === "vendor_name") {
      if (!value || !value.trim()) {
        errors.vendor_name = "Vendor name is required";
      } else if (value.trim().length < 2) {
        errors.vendor_name = "Vendor name must be at least 2 characters";
      } else if (value.length > 45) {
        errors.vendor_name = "Vendor name cannot exceed 45 characters";
      } else if (!/^[a-zA-Z0-9 .\-]+$/.test(value)) {
        errors.vendor_name = "Only letters, numbers, spaces, dots, hyphens";
      } else {
        delete errors.vendor_name;
      }
    }

    if (name === "contact_person") {
      if (!value || !value.trim()) {
        delete errors.contact_person;
      } else if (value.trim().length < 2) {
        errors.contact_person = "Contact person must be at least 2 characters";
      } else if (value.length > 45) {
        errors.contact_person = "Contact person cannot exceed 45 characters";
      } else if (!/^[a-zA-Z ]+$/.test(value)) {
        errors.contact_person = "Only letters and spaces allowed";
      } else {
        delete errors.contact_person;
      }
    }

    if (name === "contact_no") {
      if (!value || !value.trim()) {
        delete errors.contact_no;
        delete warnings.contact_no;
      } else {
        const cleaned = value.replace(/[\s\-]/g, "");
        if (!/^[+]?[0-9]{10,15}$/.test(cleaned)) {
          errors.contact_no = "Enter valid phone number (10-15 digits, digits only)";
          delete warnings.contact_no;
        } else {
          delete errors.contact_no;
          if (/^0/.test(cleaned) && !cleaned.startsWith("+")) {
            warnings.contact_no =
              "Looks like a local number — include country code if international";
          } else {
            delete warnings.contact_no;
          }
        }
      }
    }

    if (name === "address") {
      if (value && value.length > 100) {
        errors.address = "Address cannot exceed 100 characters";
      } else {
        delete errors.address;
      }
    }

    if (name === "comments") {
      if (value && value.length > 1000) {
        errors.comments = "Comments cannot exceed 1000 characters";
      } else {
        delete errors.comments;
      }
    }

    this.setState({ vendorErrors: errors, vendorWarnings: warnings });
  };

  validateVendorForm = () => {
    const { vendorFormData: formData } = this.state;
    const errors = {};

    if (!formData.vendor_name || !formData.vendor_name.trim()) {
      errors.vendor_name = "Vendor name is required";
    } else if (formData.vendor_name.trim().length < 2) {
      errors.vendor_name = "Vendor name must be at least 2 characters";
    } else if (formData.vendor_name.length > 45) {
      errors.vendor_name = "Vendor name cannot exceed 45 characters";
    } else if (!/^[a-zA-Z0-9 .\-]+$/.test(formData.vendor_name)) {
      errors.vendor_name = "Only letters, numbers, spaces, dots, hyphens";
    }

    if (formData.contact_person && formData.contact_person.trim()) {
      if (formData.contact_person.trim().length < 2) {
        errors.contact_person = "Contact person must be at least 2 characters";
      } else if (formData.contact_person.length > 45) {
        errors.contact_person = "Contact person cannot exceed 45 characters";
      } else if (!/^[a-zA-Z ]+$/.test(formData.contact_person)) {
        errors.contact_person = "Only letters and spaces allowed";
      }
    }

    if (formData.contact_no && formData.contact_no.trim()) {
      const cleaned = formData.contact_no.replace(/[\s\-]/g, "");
      if (!/^[+]?[0-9]{10,15}$/.test(cleaned)) {
        errors.contact_no = "Enter valid phone number (10-15 digits, digits only)";
      }
    }

    if (formData.address && formData.address.length > 100) {
      errors.address = "Address cannot exceed 100 characters";
    }

    if (formData.comments && formData.comments.length > 1000) {
      errors.comments = "Comments cannot exceed 1000 characters";
    }

    return errors;
  };

  // ══════════════════════════════════════════════════════════════════════════
  //  Vendor — Columns & Handlers
  // ══════════════════════════════════════════════════════════════════════════

  vendorColumns() {
    return [
      {
        dataField: "id",
        text: "#",
        formatter: (cell, row, rowIndex) => rowIndex + 1,
        sort: true,
        headerStyle: { width: "110px", textAlign: "center" },
        style: { textAlign: "center" },
      },
      {
        dataField: "vendor_name",
        text: "Vendor Name",
        sort: true,
        formatter: (cell) => cell,
      },
      {
        dataField: "contact_person",
        text: "Contact Person",
        sort: true,
        formatter: (cell) => cell || "—",
      },
      {
        dataField: "contact_no",
        text: "Contact Number",
        sort: true,
        formatter: (cell) => cell || "—",
      },
      {
        dataField: "address",
        text: "Office Address",
        sort: true,
        formatter: (cell) => cell || "—",
      },
      {
        dataField: "comments",
        text: "Comments / Notes",
        sort: true,
        formatter: (cell) => (
          <span style={{ fontStyle: "italic", color: "#64748b" }}>
            {cell || "—"}
          </span>
        ),
      },
      {
        dataField: "actions",
        text: "Actions",
        headerStyle: { width: "120px", textAlign: "center" },
        style: { textAlign: "center" },
        formatter: (cell, row) => (
          <div>
            <button
              onClick={() => this.handleVendorOpenModal(row)}
              style={{
                background: "none",
                border: "none",
                color: "#6366f1",
                marginRight: "12px",
                cursor: "pointer",
              }}
            >
              <i className="fal fa-edit" />
            </button>
            <button
              onClick={() => this.handleVendorDelete(row.id, row.vendor_name)}
              style={{
                background: "none",
                border: "none",
                color: "#ef4444",
                cursor: "pointer",
              }}
            >
              <i className="fal fa-trash-alt" />
            </button>
          </div>
        ),
      },
    ];
  }

  handleVendorOpenModal = (vendor = null) => {
    if (!this.guard.requireEdit()) return;

    if (vendor) {
      this.setState({
        vendorShowModal: true,
        vendorEditItem: vendor,
        vendorFormData: {
          vendor_name: vendor.vendor_name || "",
          contact_person: vendor.contact_person || "",
          contact_no: vendor.contact_no || "",
          address: vendor.address || "",
          comments: vendor.comments || "",
        },
        vendorErrors: {},
        vendorWarnings: {},
      });
    } else {
      this.setState({
        vendorShowModal: true,
        vendorEditItem: null,
        vendorFormData: {
          vendor_name: "",
          contact_person: "",
          contact_no: "",
          address: "",
          comments: "",
        },
        vendorErrors: {},
        vendorWarnings: {},
      });
    }
  };

  handleVendorCloseModal = () => {
    this.setState({
      vendorShowModal: false,
      vendorEditItem: null,
      vendorErrors: {},
      vendorWarnings: {},
    });
  };

  handleVendorChange = (e) => {
    const { name, value } = e.target;

    if (name === "contact_no") {
      const allowed = value.replace(/[^0-9+]/g, "");
      this.setState(
        (prev) => ({ vendorFormData: { ...prev.vendorFormData, [name]: allowed } }),
        () => this.validateVendorField(name, allowed)
      );
      return;
    }

    if (name === "contact_person") {
      const allowed = value.replace(/[^a-zA-Z ]/g, "");
      this.setState(
        (prev) => ({ vendorFormData: { ...prev.vendorFormData, [name]: allowed } }),
        () => this.validateVendorField(name, allowed)
      );
      return;
    }

    this.setState(
      (prev) => ({ vendorFormData: { ...prev.vendorFormData, [name]: value } }),
      () => this.validateVendorField(name, value)
    );
  };

  handleVendorSubmit = async (e) => {
    e.preventDefault();

    if (!this.guard.requireEdit()) return;

    const errors = this.validateVendorForm();
    if (Object.keys(errors).length > 0) {
      this.setState({ vendorErrors: errors });
      return;
    }

    const { vendorFormData: formData, vendorEditItem: editItem } = this.state;
    const { client, onUpdate } = this.props;
    const instituteId = parseInt(
      Cookies.get("institutionid") || Cookies.get("institute_id") || "0"
    );

    this.setState({ vendorSaving: true });
    try {
      if (editItem) {
        await client.mutate({
          mutation: UPDATE_VENDOR,
          variables: {
            input: {
              id: editItem.id,
              vendor_name: formData.vendor_name,
              contact_person: formData.contact_person,
              contact_no: formData.contact_no,
              address: formData.address,
              comments: formData.comments,
              institute_id: instituteId,
            },
          },
        });
        alert("Vendor updated successfully!");
      } else {
        await client.mutate({
          mutation: CREATE_VENDOR,
          variables: {
            input: {
              vendor_name: formData.vendor_name,
              contact_person: formData.contact_person,
              contact_no: formData.contact_no,
              address: formData.address,
              comments: formData.comments,
              institute_id: instituteId,
            },
          },
        });
        alert("Vendor added successfully!");
      }
      this.handleVendorCloseModal();
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error("Error saving vendor:", err);
      alert("Failed to save vendor: " + err.message);
    }
    this.setState({ vendorSaving: false });
  };

  handleVendorDelete = async (id, name) => {
    // Checked BEFORE the confirm prompt — same reasoning as handleDelete.
    if (!this.guard.requireDelete()) return;

    if (
      !window.confirm(
        `Are you sure you want to delete vendor "${name}"?\nThis action cannot be undone.`
      )
    )
      return;

    const { client, onUpdate } = this.props;
    try {
      await client.mutate({ mutation: DELETE_VENDOR, variables: { id } });
      alert("Vendor deleted successfully!");
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error("Error deleting vendor:", err);
      alert("Failed to delete vendor: " + err.message);
    }
  };

  // ══════════════════════════════════════════════════════════════════════════
  //  Shared accent-banner modal header
  // ══════════════════════════════════════════════════════════════════════════

  renderModalHeader(icon, title, onClose) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "14px",
          padding: "18px 22px",
          background: "linear-gradient(135deg, rgb(99, 102, 241), rgb(139, 92, 246))",
          color: "#fff",
        }}
      >
        <span
          style={{
            width: 40,
            height: 40,
            borderRadius: "11px",
            background: "rgba(255, 255, 255, 0.18)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "17px",
            flexShrink: 0,
          }}
        >
          <i className={icon} />
        </span>
        <span style={{ flex: 1, fontSize: "16px", fontWeight: 700, lineHeight: 1.25 }}>
          {title}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            background: "rgba(255, 255, 255, 0.18)",
            border: "none",
            color: "#fff",
            width: 32,
            height: 32,
            borderRadius: "9px",
            cursor: "pointer",
            fontSize: "15px",
            lineHeight: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <i className="fal fa-times" />
        </button>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  //  Render
  // ══════════════════════════════════════════════════════════════════════════

  render() {
    // is_view gate — self-contained so this tab denies viewing on its own, not only via
    // the ExpenseManagementMain container. Above any prop/state dereference so a denied
    // role never renders the category/vendor lists.
    if (!this.guard.canView()) {
      return <AccessDeniedCard message="You do not have permission to view Categories & Vendors." />;
    }

    const {
      subTab,
      showModal, name, description, saving, editItem, errors, successMsg,
      vendorShowModal, vendorEditItem, vendorFormData, vendorSaving,
      vendorErrors, vendorWarnings,
    } = this.state;

    const { configs } = this.props;
    const items =
      subTab === "heads"
        ? configs?.getExpenseHeads || []
        : configs?.getExpenseCategories || [];
    const vendors = configs?.getExpenseVendors || [];

    const hasErrors = errors && Object.keys(errors).length > 0;
    const hasVendorErrors = vendorErrors && Object.keys(vendorErrors).length > 0;

    const labelStyle = {
      fontSize: "13px",
      fontWeight: "600",
      color: THEME.textMuted,
      marginBottom: "5px",
      display: "block",
    };

    return (
      <div>
        {/* ─── Success banner (replaces alert popups) ──────────────────── */}
        {successMsg && (
          <div style={{
            display: "flex", alignItems: "center", gap: 10,
            background: "#ecfdf5", border: "1px solid #a7f3d0", color: "#047857",
            borderRadius: 10, padding: "10px 16px", marginBottom: 16,
            fontSize: 14, fontWeight: 600,
          }}>
            <i className="fal fa-check-circle" style={{ fontSize: 16 }} />
            {successMsg}
            <i className="fal fa-times" onClick={() => this.setState({ successMsg: "" })}
               style={{ marginLeft: "auto", cursor: "pointer", color: "#059669" }} />
          </div>
        )}
        {/* ─── Tab Bar ─────────────────────────────────────────────────── */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "24px",
            borderBottom: `1px solid ${THEME.border}`,
            paddingBottom: "2px",
          }}
        >
          <ul
            className="nav nav-tabs"
            style={{ borderBottom: "none", marginBottom: "-2px", gap: "8px" }}
          >
            {["heads", "categories", "vendors"].map((tab) => {
              const label =
                tab === "heads"
                  ? "Categories"
                  : tab === "categories"
                    ? "Sub Categories"
                    : "Vendors";
              return (
                <li className="nav-item" key={tab}>
                  <a
                    className={`nav-link ${subTab === tab ? "active" : ""}`}
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      this.setState({ subTab: tab });
                    }}
                    style={{
                      border: "none",
                      borderBottom:
                        subTab === tab
                          ? `3px solid ${THEME.primary}`
                          : "3px solid transparent",
                      color: subTab === tab ? THEME.primary : THEME.textMuted,
                      fontWeight: "700",
                      fontSize: "14px",
                      padding: "10px 16px",
                      background: "transparent",
                    }}
                  >
                    {label}
                  </a>
                </li>
              );
            })}
          </ul>

          {/* ─── Add Button (all tabs) ──────────────────────────────── */}
          <button
            onClick={() =>
              subTab === "vendors"
                ? this.handleVendorOpenModal()
                : this.handleOpenModal()
            }
            style={{
              background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
              color: "#fff",
              border: "none",
              padding: "10px 20px",
              borderRadius: "8px",
              fontSize: "13px",
              fontWeight: "700",
              minWidth: "180px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: `0 4px 12px ${THEME.primary}33`,
              transition: "all 0.2s",
              marginBottom: "8px",
            }}
          >
            <i className="fal fa-plus mr-2" />
            {subTab === "heads"
              ? "Add Category"
              : subTab === "categories"
                ? "Add Sub Category"
                : "Add Vendor"}
          </button>
        </div>

        {/* ─── Vendors Tab ─────────────────────────────────────────────── */}
        {subTab === "vendors" && (
          <>
            <div style={{ paddingTop: "8px" }}>
              <CardLessDataTable
                parentData={vendors}
                particlecolumns={this.vendorColumns()}
                search={true}
                name="expense_vendors_list"
                placeholder="Search Vendors..."
              />
            </div>

            {/* Vendor Modal */}
            <Modal
              show={vendorShowModal}
              onHide={this.handleVendorCloseModal}
              centered
              backdrop="static"
              dialogClassName="exp-modal-dialog"
            >
              {this.renderModalHeader(
                "fal fa-store",
                vendorEditItem ? "Edit Vendor Details" : "Register New Vendor",
                this.handleVendorCloseModal
              )}
              <Form onSubmit={this.handleVendorSubmit}>
                <Modal.Body className="p-4">

                  {/* Vendor Name */}
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>Vendor Name *</Form.Label>
                    <Form.Control
                      type="text"
                      name="vendor_name"
                      value={vendorFormData.vendor_name}
                      onChange={this.handleVendorChange}
                      placeholder="e.g. Acme Supplies Pvt Ltd"
                      style={{ border: `1px solid ${THEME.border}`, borderRadius: "6px", padding: "10px" }}
                      required
                    />
                    {vendorErrors?.vendor_name && (
                      <p className="field-error" style={{ color: "red", fontSize: 12, marginTop: 4 }}>
                        ⛔ {vendorErrors.vendor_name}
                      </p>
                    )}
                  </Form.Group>

                  {/* Contact Person */}
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>Contact Person</Form.Label>
                    <Form.Control
                      type="text"
                      name="contact_person"
                      value={vendorFormData.contact_person}
                      onChange={this.handleVendorChange}
                      placeholder="e.g. John Doe"
                      style={{ border: `1px solid ${THEME.border}`, borderRadius: "6px", padding: "10px" }}
                    />
                    {vendorErrors?.contact_person && (
                      <p className="field-error" style={{ color: "red", fontSize: 12, marginTop: 4 }}>
                        ⛔ {vendorErrors.contact_person}
                      </p>
                    )}
                  </Form.Group>

                  {/* Contact Number */}
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>Contact Number</Form.Label>
                    <Form.Control
                      type="text"
                      name="contact_no"
                      value={vendorFormData.contact_no}
                      onChange={this.handleVendorChange}
                      placeholder="e.g. +91 98765 43210"
                      style={{ border: `1px solid ${THEME.border}`, borderRadius: "6px", padding: "10px" }}
                    />
                    {vendorErrors?.contact_no && (
                      <p className="field-error" style={{ color: "red", fontSize: 12, marginTop: 4 }}>
                        ⛔ {vendorErrors.contact_no}
                      </p>
                    )}
                    {vendorWarnings?.contact_no && (
                      <p className="field-warning" style={{ color: "#e65c00", fontSize: 12, marginTop: 4 }}>
                        ⚠️ {vendorWarnings.contact_no}
                      </p>
                    )}
                  </Form.Group>

                  {/* Office Address */}
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>Office Address</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      name="address"
                      value={vendorFormData.address}
                      onChange={this.handleVendorChange}
                      maxLength={100}
                      placeholder="Enter complete postal address..."
                      style={{ border: `1px solid ${THEME.border}`, borderRadius: "6px", padding: "10px" }}
                    />
                    {vendorErrors?.address && (
                      <p className="field-error" style={{ color: "red", fontSize: 12, marginTop: 4 }}>
                        ⛔ {vendorErrors.address}
                      </p>
                    )}
                  </Form.Group>

                  {/* Internal Comments */}
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>Internal Comments</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      name="comments"
                      value={vendorFormData.comments}
                      onChange={this.handleVendorChange}
                      maxLength={1000}
                      placeholder="e.g. Primary stationery supplier..."
                      style={{ border: `1px solid ${THEME.border}`, borderRadius: "6px", padding: "10px" }}
                    />
                    <div className="d-flex justify-content-between align-items-center mt-1">
                      <div>
                        {vendorErrors?.comments && (
                          <p className="field-error" style={{ color: "red", fontSize: 12, margin: 0 }}>
                            ⛔ {vendorErrors.comments}
                          </p>
                        )}
                      </div>
                      <small
                        style={{
                          color:
                            (vendorFormData.comments || "").length > 900
                              ? (vendorFormData.comments || "").length === 1000
                                ? "red"
                                : "orange"
                              : "#888",
                          fontWeight: "600",
                        }}
                      >
                        {(vendorFormData.comments || "").length} / 1000
                      </small>
                    </div>
                  </Form.Group>

                </Modal.Body>
                <Modal.Footer style={{ borderTop: `1px solid ${THEME.border}` }}>
                  <Button
                    variant="light"
                    onClick={this.handleVendorCloseModal}
                    disabled={vendorSaving}
                    style={{ fontWeight: "700" }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={vendorSaving || hasVendorErrors}
                    style={{ background: "linear-gradient(135deg, #10b981 0%, #059669 100%)", border: "none", fontWeight: "700", padding: "8px 24px" }}
                  >
                    {vendorSaving ? (
                      <>
                        <Spinner size="sm" animation="border" className="mr-1" /> Saving...
                      </>
                    ) : (
                      "Save Vendor"
                    )}
                  </Button>
                </Modal.Footer>
              </Form>
            </Modal>
          </>
        )}

        {/* ─── Categories / Sub-Categories Tab ────────────────────────── */}
        {subTab !== "vendors" && (
          <>
            <div style={{ paddingTop: "8px" }}>
              <CardLessDataTable
                parentData={items}
                particlecolumns={this.getColumns()}
                search={true}
                name={
                  subTab === "heads"
                    ? "expense_heads_list"
                    : "expense_categories_list"
                }
                placeholder={
                  subTab === "heads"
                    ? "Search Categories..."
                    : "Search Sub Categories..."
                }
              />
            </div>

            {/* Category / Sub-Category Modal */}
            <Modal
              show={showModal}
              onHide={this.handleCloseModal}
              centered
              dialogClassName="exp-modal-dialog"
            >
              {this.renderModalHeader(
                subTab === "heads" ? "fal fa-tags" : "fal fa-tag",
                `${editItem ? "Edit" : "Add New"} ${subTab === "heads" ? "Category" : "Sub Category"}`,
                this.handleCloseModal
              )}
              <Form onSubmit={this.handleSubmit}>
                <Modal.Body className="p-4">
                  <Form.Group className="mb-3">
                    <Form.Label
                      style={{ fontSize: "13px", fontWeight: "600", color: THEME.textMuted }}
                    >
                      Name *
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      placeholder={`e.g. ${subTab === "heads" ? "Office Supplies" : "Stationery & Pens"
                        }`}
                      value={name}
                      onChange={this.handleChange}
                      required
                      style={{
                        border: `1px solid ${THEME.border}`,
                        borderRadius: "8px",
                        padding: "10px 14px",
                        fontSize: "14px",
                      }}
                    />
                    {errors?.name && (
                      <p className="field-error" style={{ color: "red", fontSize: 12, marginTop: 4 }}>
                        ⛔ {errors.name}
                      </p>
                    )}
                  </Form.Group>

                  {subTab !== "heads" && (
                    <Form.Group className="mb-3">
                      <Form.Label
                        style={{ fontSize: "13px", fontWeight: "600", color: THEME.textMuted }}
                      >
                        Category *
                      </Form.Label>
                      <Form.Control
                        as="select"
                        name="selectedHeadId"
                        value={this.state.selectedHeadId}
                        onChange={(e) => this.setState({ selectedHeadId: e.target.value })}
                        required
                        style={{
                          border: `1px solid ${THEME.border}`,
                          borderRadius: "8px",
                          padding: "10px 14px",
                          fontSize: "14px",
                        }}
                      >
                        <option value="">Select Category</option>
                        {(this.props.configs?.getExpenseHeads || []).map(h => (
                          <option key={h.id} value={h.id}>{h.expense_head}</option>
                        ))}
                      </Form.Control>
                      {errors?.selectedHeadId && (
                        <p className="field-error" style={{ color: "red", fontSize: 12, marginTop: 4 }}>
                          ⛔ {errors.selectedHeadId}
                        </p>
                      )}
                    </Form.Group>
                  )}

                  {subTab !== "heads" && (
                    <Form.Group className="mb-3">
                      <Form.Label
                        style={{ fontSize: "13px", fontWeight: "600", color: THEME.textMuted }}
                      >
                        Description
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="description"
                        placeholder="Optional brief description..."
                        value={description}
                        onChange={this.handleChange}
                        maxLength={500}
                        style={{
                          border: `1px solid ${THEME.border}`,
                          borderRadius: "8px",
                          padding: "10px 14px",
                          fontSize: "14px",
                        }}
                      />
                      <div className="d-flex justify-content-between align-items-center mt-1">
                        <div>
                          {errors?.description && (
                            <p className="field-error" style={{ color: "red", fontSize: 12, margin: 0 }}>
                              ⛔ {errors.description}
                            </p>
                          )}
                        </div>
                        <small
                          style={{
                            color:
                              (description || "").length > 450
                                ? (description || "").length === 500
                                  ? "red"
                                  : "orange"
                                : "#888",
                            fontWeight: "600",
                          }}
                        >
                          {(description || "").length} / 500
                        </small>
                      </div>
                    </Form.Group>
                  )}
                </Modal.Body>
                <Modal.Footer style={{ borderTop: `1px solid ${THEME.border}` }}>
                  <Button
                    variant="link"
                    onClick={this.handleCloseModal}
                    style={{
                      color: THEME.textMuted,
                      fontSize: "13.5px",
                      fontWeight: "600",
                      textDecoration: "none",
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving || hasErrors}
                    style={{
                      background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                      border: "none",
                      borderRadius: "8px",
                      fontSize: "13.5px",
                      fontWeight: "700",
                      padding: "8px 24px",
                    }}
                  >
                    {saving ? (
                      <Spinner size="sm" animation="border" variant="light" />
                    ) : (
                      "Save Changes"
                    )}
                  </Button>
                </Modal.Footer>
              </Form>
            </Modal>
          </>
        )}

        {/* Shown instead of creating / editing / deleting a category or vendor when the
            grant is missing. One modal serves every intercepted action on this tab. */}
        <AccessDeniedModal
          show={this.state.accessDeniedShow}
          onHide={this.guard.hideAccessDenied}
        />
      </div>
    );
  }
}

export default ExpenseTypes;